import { useContext, useEffect, useCallback, useState } from 'react';
import { PusherContext } from '../providers/PusherProvider';
import { AuthContext } from '../contexts/AuthContext';

/**
 * Hook for listening to price alerts via Pusher
 * 
 * Subscribes to the user-{userId}-alerts channel and listens for:
 * - price-alert: When a price alert is triggered
 */
const usePusherPriceAlerts = (onAlert) => {
  const pusher = useContext(PusherContext);
  const { user } = useContext(AuthContext);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [lastAlert, setLastAlert] = useState(null);

  // Handle incoming alert
  const handleAlert = useCallback((data) => {
    setLastAlert(data);
    
    if (onAlert && typeof onAlert === 'function') {
      onAlert(data);
    } else {
      console.warn('[usePusherPriceAlerts] No onAlert callback provided!');
    }
  }, [onAlert]);

  useEffect(() => {
    if (!pusher) {
      setIsSubscribed(false);
      return;
    }
    
    if (!user?.id) {
      setIsSubscribed(false);
      return;
    }

    // Using public channel for now (no auth required)
    // TODO: Switch to private-user-{userId} with Pusher auth for production
    const channelName = `user-${user.id}-alerts`;
    
    // Check if already subscribed
    let channel = pusher.channel(channelName);
    
    if (!channel) {
      // Subscribe to the channel
      channel = pusher.subscribe(channelName);
      
      // Log subscription errors only
      channel.bind('pusher:subscription_error', (status) => {
        console.error(`[usePusherPriceAlerts] Subscription error for ${channelName}:`, status);
      });
    }

    // Bind to the price-alert event
    channel.bind('price-alert', handleAlert);
    setIsSubscribed(true);

    // Cleanup on unmount
    return () => {
      if (channel) {
        channel.unbind('price-alert', handleAlert);
      }
    };
  }, [pusher, user?.id, handleAlert]);

  return {
    isSubscribed,
    lastAlert
  };
};

export default usePusherPriceAlerts;








